'use client'
import { type FormEventHandler, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { GdprBeforeOfferPrivacyLink } from '@/components/GdprBeforeOfferPrivacyLink'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import {
  type FormError,
  PRODUCT_FIELDNAME,
  type ProductOption,
  ProductPurchaseForm,
  type ProductPurchaseFormProps,
  SSN_FIELDNAME,
} from '@/components/ProductPurchaseForm/ProductPurchaseForm'
import { useRedeemCampaignMutation, useShopSessionCustomerUpdateMutation } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { setupShopSessionServiceClientSide } from '@/services/shopSession/ShopSession.helpers'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { useCurrentCountry } from '@/utils/l10n/useCurrentCountry'
import { useApolloClient } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { type ProductData } from '../ProductData/ProductData.types'
import { wrapper } from './ProductPurchase.css'

type ForwardedFormProps = Pick<
  ProductPurchaseFormProps,
  'productByline' | 'showHeader' | 'promoteInboundSales' | 'ribbonText'
>

type ProductPurchaseProps = {
  products: Array<string>
  campaignCode?: string
  // Partner attribution. When set, a fresh shop session is created so attribution can influence pricing.
  attributedTo?: string
  preSelectFirstProduct?: boolean
  nested?: boolean
  className?: string
} & ForwardedFormProps

export function ProductPurchase({
  products,
  campaignCode,
  attributedTo,
  preSelectFirstProduct,
  nested,
  className,
  ...forwardedProps
}: ProductPurchaseProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<FormError>()

  const router = useRouter()
  const { t } = useTranslation(['common', 'purchase-form'])

  const apolloClient = useApolloClient()
  const { countryCode } = useCurrentCountry()
  const { shopSession: currentShopSession } = useShopSession()
  const productMetadata = useProductMetadata()

  const [updateCustomer] = useShopSessionCustomerUpdateMutation()
  const [redeemCampaign] = useRedeemCampaignMutation()

  const productOptions = useMemo(() => {
    const result: Array<ProductOption & Pick<ProductData, 'pageLink' | 'priceCalculatorPageLink'>> = []
    products.forEach((productName) => {
      const matchedProduct = productMetadata?.find((product) => product.name === productName)
      if (matchedProduct) {
        result.push({
          name: matchedProduct.displayNameShort,
          value: matchedProduct.name,
          img: {
            src: matchedProduct.pillowImage.src,
            alt: matchedProduct.pillowImage.alt ?? '',
          },
          pageLink: matchedProduct.pageLink,
          priceCalculatorPageLink: matchedProduct.priceCalculatorPageLink,
        })
      }
    })

    return result
  }, [products, productMetadata])

  if (productOptions.length === 0) {
    throw new Error('ProductPurchase | no product options found')
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    try {
      setError({})
      setIsSubmitting(true)

      const formData = new FormData(event.currentTarget)
      const ssn = formData.get(SSN_FIELDNAME)
      const productName = formData.get(PRODUCT_FIELDNAME)

      // This should never happen since ProductPurchaseForm controls are required
      if (typeof ssn !== 'string' || typeof productName !== 'string') {
        throw new Error('ProductPurchase | ssn and product are required')
      }

      let shopSession = currentShopSession
      // Partner attribution can influence pricing, so we need to create a new shop session.
      // This is only intended for partner pages
      if (attributedTo) {
        datadogLogs.setGlobalContextProperty('attributedTo', attributedTo)
        logger.info(`ProductPurchase | create shop session attributed to ${attributedTo}`)
        const shopSessionService = setupShopSessionServiceClientSide(apolloClient)
        shopSession = await shopSessionService.create({
          countryCode,
          attribution: { attributedTo },
        })
        shopSessionService.saveId(shopSession.id)
      }

      if (!shopSession) {
        setError((prevState) => ({ ...prevState, general: t('UNKNOWN_ERROR_MESSAGE') }))
        throw new Error('ProductPurchase | shop session not found')
      }

      const priceCalculatorPageLink = productOptions.find(
        (product) => product.value === productName,
      )?.priceCalculatorPageLink
      if (priceCalculatorPageLink == null) {
        setError((prevState) => ({ ...prevState, general: t('UNKNOWN_ERROR_MESSAGE') }))
        throw new Error(`ProductPurchase | PDP link for ${productName} not found`)
      }

      // Pre-fetches Price Calculator page - instant transitions
      router.prefetch(priceCalculatorPageLink)

      if (ssn && ssn !== shopSession.customer?.ssn) {
        await updateCustomer({
          variables: {
            input: {
              shopSessionId: shopSession.id,
              ssn,
            },
          },
          onError(error) {
            const message = error.extraInfo?.userError?.message
            // TODO: use useAppErrorHandleContext or not even throw an error here.
            // It would be probably fine to redirect users even if we failed on updating
            // their ssn.
            setError((prevState) => {
              if (message) {
                return { ...prevState, ssn: message }
              }
              return { ...prevState, general: t('UNKNOWN_ERROR_MESSAGE') }
            })
            throw new Error(`ProductPurchase | failed to update customer: ${error.message}`)
          },
        })
      }

      if (campaignCode) {
        redeemCampaign({
          variables: {
            shopSessionId: shopSession.id,
            code: campaignCode,
          },
          onError(error) {
            logger.error(`ProductPurchase | failed to redeem code ${campaignCode}: ${error.message}`)
          },
        })
      }

      const url = new URL(priceCalculatorPageLink, window.location.origin)
      if (shopSession.id !== currentShopSession?.id) {
        // Opt out of client side nav to make sure we use the latest shop session
        window.location.assign(url.toString())
      } else {
        router.push(url.toString())
      }
    } catch (error) {
      setIsSubmitting(false)
      logger.error((error as Error).message)
    }
  }

  return (
    <div className={clsx(wrapper, className)} data-nested={nested}>
      <ProductPurchaseForm
        {...forwardedProps}
        onSubmit={handleSubmit}
        productOptions={productOptions}
        defaultValue={preSelectFirstProduct ? productOptions[0].value : undefined}
        submitting={isSubmitting}
        ssnDefaultValue={currentShopSession?.customer?.ssn ?? ''}
        error={error}
      />
      <GdprBeforeOfferPrivacyLink />
    </div>
  )
}
